import { useState, useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useNavigate } from "react-router-dom";
import { fetchSolutions, type SolutionItem } from "../../api";

const fallbackSolutions = [
  {
    id: "construction",
    title: "Construction & Engineering",
    shortTitle: "Construction",
    description: "Multi-floor sites, tunnels, and remote locations demand radios that survive dust, drops, and extreme weather. Our rugged series keeps every trade connected with dedicated channels and GPS tracking.",
    features: [
      "Multi-channel coordination for 20+ trades",
      "5km to 35km range coverage for sprawling sites",
      "Emergency alert and man-down detection",
      "Real-time GPS workforce tracking",
    ],
    image: "https://readdy.ai/api/search-image?query=Large%20construction%20site%20with%20cranes%20and%20steel%20structures%20workers%20wearing%20safety%20helmets%20using%20communication%20devices%20blue%20sky%20professional%20industrial%20photography%20wide%20angle&width=800&height=600&seq=120&orientation=squarish",
    stat: "2,500+",
    statLabel: "Active Sites",
    icon: "ri-building-line",
    color: "primary",
  },
  {
    id: "logistics",
    title: "Logistics & Transportation",
    shortTitle: "Logistics",
    description: "Coordinate fleets, warehouses, and distribution centers with instant voice communication. Noise-canceling technology ensures drivers and warehouse staff stay connected even in the loudest environments.",
    features: [
      "Fleet-wide instant push-to-talk",
      "AI noise cancellation for warehouse floors",
      "24-hour battery for long-haul operations",
      "Dispatch system integration APIs",
    ],
    image: "https://readdy.ai/api/search-image?query=Modern%20logistics%20warehouse%20interior%20with%20automated%20shelving%20and%20conveyor%20belts%20workers%20using%20handheld%20devices%20professional%20industrial%20photography%20blue%20and%20green%20color%20tones&width=800&height=600&seq=121&orientation=squarish",
    stat: "500+",
    statLabel: "Fleet Deployments",
    icon: "ri-truck-line",
    color: "secondary",
  },
  {
    id: "security",
    title: "Security & Event Management",
    shortTitle: "Security",
    description: "Seamless coordination for security teams at concerts, sports events, and critical infrastructure. Encrypted channels and discrete earpieces keep operations confidential and efficient.",
    features: [
      "AES-256 encrypted channels",
      "Discrete earpiece and mic compatibility",
      "Instant group and private calling",
      "One-touch panic button with GPS",
    ],
    image: "https://readdy.ai/api/search-image?query=Security%20team%20professionals%20at%20large%20outdoor%20event%20venue%20with%20crowd%20control%20barriers%20professional%20security%20personnel%20using%20communication%20equipment%20blue%20and%20green%20color%20tones%20professional%20photography&width=800&height=600&seq=122&orientation=squarish",
    stat: "10K+",
    statLabel: "Events Secured",
    icon: "ri-shield-check-line",
    color: "primary",
  },
  {
    id: "hospitality",
    title: "Hospitality & Tourism",
    shortTitle: "Hospitality",
    description: "Elevate guest experiences by keeping hotel staff, tour guides, and resort teams seamlessly connected. Lightweight, discreet devices with multi-floor coverage ensure prompt service.",
    features: [
      "Lightweight under-200g design",
      "Multi-floor DAS repeater support",
      "Housekeeping and concierge coordination",
      "PMS integration via middleware",
    ],
    image: "https://readdy.ai/api/search-image?query=Luxury%20hotel%20lobby%20with%20elegant%20interior%20design%20concierge%20desk%20staff%20professional%20hospitality%20environment%20blue%20and%20green%20accent%20lighting%20professional%20photography&width=800&height=600&seq=123&orientation=squarish",
    stat: "800+",
    statLabel: "Hotel Partners",
    icon: "ri-hotel-line",
    color: "secondary",
  },
  {
    id: "manufacturing",
    title: "Manufacturing & Industrial",
    shortTitle: "Manufacturing",
    description: "Maintain production line efficiency with instant communication between operators, supervisors, and maintenance teams. Intrinsically safe options available for hazardous environments.",
    features: [
      "Intrinsically safe ATEX/IECEx certified",
      "105dB loud audio for noisy factories",
      "Text messaging for non-disruptive alerts",
      "CMMS integration for maintenance",
    ],
    image: "https://readdy.ai/api/search-image?query=Modern%20manufacturing%20factory%20floor%20with%20robotic%20arms%20and%20production%20line%20workers%20in%20safety%20gear%20professional%20industrial%20photography%20blue%20and%20green%20color%20tones&width=800&height=600&seq=124&orientation=squarish",
    stat: "350+",
    statLabel: "Plant Networks",
    icon: "ri-settings-4-line",
    color: "primary",
  },
  {
    id: "public-safety",
    title: "Public Safety & Emergency",
    shortTitle: "Public Safety",
    description: "When every second counts, reliability is everything. Built for first responders with military-grade encryption, priority interrupt, and full submersion waterproofing.",
    features: [
      "MIL-STD-810G military-grade build",
      "Priority interrupt for emergency calls",
      "IP68 submersible up to 2m for 2h",
      "P25 / DMR interoperability",
    ],
    image: "https://readdy.ai/api/search-image?query=Emergency%20response%20team%20first%20responders%20at%20night%20scene%20with%20emergency%20vehicles%20blue%20and%20red%20lights%20professional%20photography%20high%20quality%20dramatic%20lighting&width=800&height=600&seq=125&orientation=squarish",
    stat: "150+",
    statLabel: "Agencies Trust Us",
    icon: "ri-first-aid-kit-line",
    color: "secondary",
  },
];

export default function Solutions() {
  const navigate = useNavigate();
  const [solutions, setSolutions] = useState<SolutionItem[]>(fallbackSolutions);

  useEffect(() => {
    fetchSolutions()
      .then((res) => { if (res.length > 0) setSolutions(res); })
      .catch((err) => console.warn("解决方案数据加载失败:", err));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Industrial%20communication%20tower%20antennas%20at%20sunset%20with%20dramatic%20orange%20and%20blue%20sky%20professional%20photography%20wide%20angle%20landscape%20technology%20infrastructure&width=1400&height=800&seq=126&orientation=landscape"
              alt="Solutions"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/60 to-dark/30"></div>
          </div>
          <div className="relative z-10 h-full flex flex-col justify-center px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-1.5 bg-white/20 rounded-full mb-6">
                <span className="text-white text-sm font-medium">Industry Solutions</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Communication for<br />
                <span className="text-secondary">Every Industry</span>
              </h1>
              <p className="text-white/80 text-lg mt-4 max-w-2xl">
                Tailored two-way radio solutions designed for the unique challenges of your industry.
              </p>
            </div>
          </div>
        </section>

        {/* Solutions List - Alternating Layout */}
        <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto space-y-20 md:space-y-28">
            {solutions.map((solution, index) => {
              const isEven = index % 2 === 0;
              const bgColor = solution.color === "primary" ? "bg-light-blue" : "bg-light-gray";
              const accentColor = solution.color === "primary" ? "text-primary" : "text-secondary";
              const accentBg = solution.color === "primary" ? "bg-primary" : "bg-secondary";
              return (
                <div
                  key={solution.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${isEven ? "" : "lg:grid-flow-dense"}`}
                >
                  {/* Image */}
                  <div className={`relative overflow-hidden rounded-2xl ${isEven ? "" : "lg:col-start-2"}`}>
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={solution.image}
                        alt={solution.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    {/* Stat overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                      <div className="flex items-end gap-3">
                        <span className={`text-4xl md:text-5xl font-bold ${accentColor}`}>{solution.stat}</span>
                        <span className="text-white/90 text-sm font-medium pb-2">{solution.statLabel}</span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${isEven ? "" : "lg:col-start-1 lg:row-start-1"}`}>
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-gray-200 rounded-full mb-4">
                      <i className={`${solution.icon} ${accentColor} text-sm`}></i>
                      <span className="text-gray-600 text-sm font-medium">{solution.shortTitle}</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 leading-tight">
                      {solution.title}
                    </h2>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {solution.description}
                    </p>
                    <ul className="space-y-3 mb-8">
                      {solution.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                          <div className={`w-5 h-5 rounded-full ${accentBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => navigate("/contact")}
                        className={`px-6 py-3 ${accentBg} text-white text-sm font-semibold rounded-full hover:opacity-90 transition-opacity whitespace-nowrap`}
                      >
                        Request a Demo
                      </button>
                      <button
                        onClick={() => navigate("/products")}
                        className="px-6 py-3 border border-gray-300 text-dark text-sm font-semibold rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap"
                      >
                        View Products
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-light-blue py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
              Need a Custom Solution?
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Our engineering team can design a tailored communication system that fits your specific operational requirements and budget.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate("/contact")}
                className="px-8 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
              >
                Contact Our Team
              </button>
              <button
                onClick={() => navigate("/products")}
                className="px-8 py-3 border border-gray-300 text-dark font-semibold rounded-full hover:bg-white hover:border-primary transition-colors whitespace-nowrap"
              >
                Browse Products
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}